import { meterLevel, rms } from '@/core/recording/session';

export type AudioInput = 'microphone' | 'system';

interface Input {
  readonly source: MediaStreamAudioSourceNode;
  readonly analyser: AnalyserNode;
  readonly samples: Float32Array<ArrayBuffer>;
}

/**
 * Mixes the microphone and the shared tab or system sound into the single
 * audio track that is recorded, and measures each input for the level meters.
 * Web Audio keeps running in a background tab, so the mix never stalls while
 * the person works in another window.
 */
export class AudioMixer {
  private readonly context = new AudioContext({ sampleRate: 48_000, latencyHint: 'interactive' });
  private readonly destination = this.context.createMediaStreamDestination();
  private readonly inputs = new Map<AudioInput, Input>();

  /** Connects (or replaces) an input; a stream without audio removes it. */
  set(kind: AudioInput, stream: MediaStream | null): void {
    this.remove(kind);
    if (!stream || stream.getAudioTracks().length === 0) return;
    const source = this.context.createMediaStreamSource(stream);
    const analyser = this.context.createAnalyser();
    analyser.fftSize = 1024;
    source.connect(analyser);
    source.connect(this.destination);
    this.inputs.set(kind, { source, analyser, samples: new Float32Array(analyser.fftSize) });
  }

  remove(kind: AudioInput): void {
    const input = this.inputs.get(kind);
    if (!input) return;
    input.source.disconnect();
    this.inputs.delete(kind);
  }

  has(kind: AudioInput): boolean {
    return this.inputs.has(kind);
  }

  /** Current loudness of an input on a 0..1 meter scale. */
  level(kind: AudioInput): number {
    const input = this.inputs.get(kind);
    if (!input) return 0;
    input.analyser.getFloatTimeDomainData(input.samples);
    return meterLevel(rms(input.samples));
  }

  /** The mixed track to record, or null when there is nothing to hear. */
  get track(): MediaStreamTrack | null {
    return this.inputs.size > 0 ? (this.destination.stream.getAudioTracks()[0] ?? null) : null;
  }

  /** Audio contexts start suspended until a click; call from one. */
  async resume(): Promise<void> {
    if (this.context.state === 'suspended') await this.context.resume().catch(() => undefined);
  }

  close(): void {
    for (const kind of [...this.inputs.keys()]) this.remove(kind);
    void this.context.close().catch(() => undefined);
  }
}
